import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';
import { KADRY_REQUESTS_OPS, useKadryAccess, type KadryOp } from '@entities/kadry';
import { riseIn } from '@shared/theme/animations';
import { useListKadryNotificationsQuery } from '@shared/api';

/* Оболочка агента ОСНОВА.Кадры: левый рельс разделов + основная область.
   Разделы показываются по операциям пользователя из `/info` (Прил. Б) и только
   при готовом модуле; выключенный модуль — только «Обзор» с баннером. */

interface Section {
  path: string;
  label: string;
  anyOf: readonly KadryOp[];
}

// Порядок в рельсе: рабочие разделы сверху, «Настройки» — последними.
// Пустой `anyOf` — раздел виден всем, у кого есть доступ к модулю.
const SECTIONS: readonly Section[] = [
  { path: 'requests', label: 'Заявки', anyOf: KADRY_REQUESTS_OPS },
  { path: 'interested', label: 'Подходит', anyOf: ['shortlist_view'] },
  { path: 'audit', label: 'Журнал', anyOf: ['audit_log_view'] },
  { path: 'notifications', label: 'Уведомления', anyOf: [] },
  { path: 'settings', label: 'Настройки', anyOf: ['admin_settings'] },
];

const Shell = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 232px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(7)};
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing(5)};
  }
`;

const Rail = styled.nav`
  position: sticky;
  top: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};

  /* На узких экранах рельс превращается в горизонтальную ленту над контентом. */
  @media (max-width: 920px) {
    position: static;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ theme }) => theme.spacing(2)};
    -webkit-overflow-scrolling: touch;
  }
`;

const Brand = styled(Link)`
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const RailLink = styled(Link)<{ $active: boolean }>`
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border-left: 2px solid ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  @media (max-width: 920px) {
    border-left: none;
    border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
    border-radius: ${({ theme }) => theme.radius.pill};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut} both;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

export function KadryLayout() {
  const location = useLocation();
  const { can_read, can_write } = useAgentPermission('kadry');
  const { ready, canAny } = useKadryAccess();
  const segments = location.pathname.split('/').filter(Boolean); // ['agents','kadry',...]
  const isOverview = segments.length === 2;
  const section = segments[2];
  const visible = ready
    ? SECTIONS.filter((s) => s.anyOf.length === 0 || canAny(s.anyOf))
    : [];
  // Непрочитанные: ответы кандидатов и тревоги. WS-событие kadry_notification
  // инвалидирует список сразу; опрос — страховка при потере соединения.
  const unread = useListKadryNotificationsQuery(
    { unread: true },
    { skip: !ready, pollingInterval: 60000 },
  ).data?.length ?? 0;

  return (
    <Shell>
      <Rail aria-label="Разделы ОСНОВА.Кадры">
        <Brand to="/agents/kadry">
          ОСНОВА<span className="accent">.Кадры</span>
        </Brand>
        <RailLink
          to="/agents/kadry"
          $active={isOverview}
          aria-current={isOverview ? 'page' : undefined}
        >
          Обзор
        </RailLink>
        {visible.map((s) => {
          const active = section === s.path;
          return (
            <RailLink
              key={s.path}
              to={`/agents/kadry/${s.path}`}
              $active={active}
              aria-current={active ? 'page' : undefined}
            >
              {s.label}
              {s.path === 'notifications' && unread > 0 ? ` (${unread})` : ''}
            </RailLink>
          );
        })}
      </Rail>
      <Main>
        {isOverview && (
          <Header>
            <Title>
              ОСНОВА<span className="accent">.Кадры</span>
            </Title>
            <Subtitle>
              Поиск и привлечение персонала: заявки на подбор, поиск кандидатов на площадках,
              кадровый резерв и бот для кандидатов.
            </Subtitle>
          </Header>
        )}
        {can_read && !can_write && <ReadOnlyBanner />}
        <Outlet />
      </Main>
    </Shell>
  );
}
